import React, { useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppRouter } from './AppRouter';
import ToastProvider from './components/ui/ToastProvider';
const createBackofficeTheme = (mode: PaletteMode) => {
  const isDark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#5B4EF0',
        light: '#7B6FF5',
        dark: '#3D30C4',
        contrastText: '#FFFFFF'
      },
      secondary: {
        main: '#00B4B3',
        light: '#4EDBD8',
        dark: '#008C8B',
        contrastText: '#041A1B'
      },
      background: {
        default: isDark ? '#0D0C14' : '#F5F6FA',
        paper: isDark ? '#1A1830' : 'rgba(255,255,255,0.96)'
      },
      text: {
        primary: isDark ? '#F0EFFF' : '#111827',
        secondary: isDark ? '#9896C8' : '#6B7280'
      },
      error: {
        main: '#DC2626',
        light: '#FEE2E2'
      },
      success: {
        main: '#10B981',
        light: '#D1FAE5'
      },
      warning: {
        main: '#F59E0B',
        light: '#FEF3C7'
      },
      info: {
        main: '#3B82F6',
        light: '#EFF6FF'
      },
      divider: isDark ? '#2D2B50' : '#E5E7EB'
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontSize: '1.75rem',
        fontWeight: 600
      },
      h5: {
        fontSize: '1.4rem',
        fontWeight: 600
      },
      h6: {
        fontSize: '1.15rem',
        fontWeight: 600
      },
      body1: {
        fontSize: '0.9rem'
      },
      body2: {
        fontSize: '0.8rem'
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500
      },
      subtitle2: {
        fontSize: '0.85rem',
        fontWeight: 500
      }
    },
    shape: {
      borderRadius: 12
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollBehavior: 'smooth',
            boxSizing: 'border-box'
          },
          'html, body, #root': {
            minHeight: '100%'
          },
          body: {
            margin: 0,
            background: isDark ? 'radial-gradient(circle at top right, rgba(91,78,240,0.18), transparent 28%), #0D0C14' : 'radial-gradient(circle at top right, rgba(0,180,179,0.10), transparent 30%), #F5F6FA'
          },
          '#root': {
            minHeight: '100vh',
            backgroundColor: isDark ? '#0D0C14' : '#F5F6FA'
          },
          a: {
            color: 'inherit',
            textDecoration: 'none'
          },
          ...(isDark ? {
            'main, main.MuiBox-root': {
              backgroundColor: '#0D0C14 !important',
              color: '#F0EFFF'
            },
            '.MuiDrawer-paper, .MuiDrawer-paper > .MuiBox-root': {
              backgroundColor: '#13112B !important',
              color: '#F0EFFF !important',
              borderColor: '#2D2B50 !important'
            },
            '.MuiDrawer-paper .MuiTypography-root': {
              color: 'inherit'
            },
            '.MuiDrawer-paper .MuiListItemIcon-root': {
              color: '#9896C8'
            },
            '.MuiAppBar-root': {
              backgroundColor: 'rgba(19,17,43,0.94) !important',
              color: '#F0EFFF !important',
              borderColor: '#2D2B50 !important'
            },
            'main .MuiCard-root, main .MuiPaper-root:not(.MuiAppBar-root)': {
              background: 'linear-gradient(145deg, rgba(26,24,48,0.98), rgba(19,17,43,0.96)) !important',
              backgroundColor: '#1A1830 !important',
              borderColor: '#2D2B50 !important',
              color: '#F0EFFF !important'
            },
            'main .MuiCard-root .MuiTypography-root, main .MuiPaper-root:not(.MuiAppBar-root) .MuiTypography-root': {
              color: '#F0EFFF'
            },
            'main .MuiCard-root > .MuiCardContent-root > .MuiBox-root': {
              backgroundColor: 'rgba(255,255,255,0.035) !important',
              borderColor: '#2D2B50 !important'
            },
            'main .MuiOutlinedInput-root, main .MuiInputBase-root, main .MuiToggleButtonGroup-root': {
              backgroundColor: 'rgba(26,24,48,0.92) !important',
              color: '#F0EFFF !important'
            },
            'main .MuiInputBase-input, main .MuiInputLabel-root, main .MuiSelect-icon, main .MuiToggleButton-root': {
              color: '#D8D4FF !important'
            },
            'main .MuiToggleButton-root.Mui-selected': {
              backgroundColor: '#29264A !important',
              color: '#F0EFFF !important'
            },
            'main .MuiTableCell-root': {
              color: '#D8D4FF !important',
              borderColor: '#2D2B50 !important'
            },
            'main .MuiTableCell-head': {
              backgroundColor: '#13112B !important',
              color: '#F0EFFF !important'
            },
            'main .MuiTableRow-root:nth-of-type(even)': {
              backgroundColor: 'rgba(255,255,255,0.02) !important'
            },
            'main .MuiDivider-root': {
              borderColor: '#2D2B50'
            },
            'main .MuiTablePagination-root, main .MuiTablePagination-root .MuiSvgIcon-root': {
              color: '#D8D4FF'
            },
            'main .MuiSkeleton-root': {
              backgroundColor: 'rgba(255,255,255,0.10)'
            }
          } : {})
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${isDark ? '#2D2B50' : 'rgba(91,78,240,0.14)'}`,
            background: isDark ? 'linear-gradient(145deg, rgba(26,24,48,0.98), rgba(19,17,43,0.96))' : 'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(245,246,250,0.92))',
            boxShadow: isDark ? '0 18px 42px rgba(0,0,0,0.24)' : '0 18px 42px rgba(15,23,42,0.08)',
            transition: 'all 240ms ease',
            '&:hover': {
              boxShadow: isDark ? '0 20px 46px rgba(0,0,0,0.32)' : '0 20px 46px rgba(15,23,42,0.12)',
              borderColor: 'rgba(91,78,240,0.32)'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none'
          },
          elevation1: {
            boxShadow: '0 4px 12px rgba(2,6,23,0.05)'
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontSize: '0.9rem',
            borderRadius: 12,
            transition: 'all 180ms ease',
            '&.Mui-focused': {
              boxShadow: 'none'
            },
            '&:hover': {
              borderColor: '#5B4EF0'
            }
          },
          notchedOutline: {
            borderColor: isDark ? '#3D3B60' : '#D8DDF0',
            borderWidth: 1.5
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.9rem',
            fontWeight: 500
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            padding: '8px 18px',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 200ms ease',
            '&:focus-visible': {
              boxShadow: '0 0 0 2px rgba(91,78,240,0.36)',
              outline: 'none'
            }
          },
          contained: {
            background: 'linear-gradient(135deg, rgba(91,78,240,0.98), rgba(0,180,179,0.92))',
            border: '1px solid rgba(255,255,255,0.22)',
            boxShadow: '0 10px 24px rgba(91,78,240,0.24)',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(61,48,196,1), rgba(0,140,139,0.96))',
              boxShadow: '0 12px 28px rgba(91,78,240,0.32)'
            },
            '&.Mui-disabled': {
              background: '#E2E8F0',
              color: '#94A3B8'
            }
          },
          outlined: {
            borderColor: 'rgba(91,78,240,0.24)',
            borderWidth: 1.5,
            background: `${isDark ? 'rgba(26,24,48,0.76)' : 'rgba(255,255,255,0.72)'} padding-box, linear-gradient(135deg, rgba(91,78,240,0.62), rgba(0,180,179,0.54)) border-box`,
            '&:hover': {
              borderColor: 'transparent',
              background: `${isDark ? 'rgba(45,43,80,0.88)' : 'rgba(245,246,250,0.96)'} padding-box, linear-gradient(135deg, rgba(91,78,240,0.9), rgba(0,180,179,0.72)) border-box`,
              boxShadow: '0 8px 18px rgba(91,78,240,0.16)'
            }
          },
          text: {
            '&:hover': {
              backgroundColor: 'rgba(91,78,240,0.1)'
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600,
            fontSize: '0.75rem',
            transition: 'all 200ms ease'
          },
          filled: {
            backgroundColor: 'rgba(91,78,240,0.12)',
            color: isDark ? '#D8D4FF' : '#4C3EE3',
            border: '1px solid transparent',
            background: 'rgba(91,78,240,0.12) padding-box, linear-gradient(135deg, rgba(91,78,240,0.42), rgba(0,180,179,0.35)) border-box'
          },
          outlined: {
            borderWidth: 1.5,
            background: 'transparent padding-box, linear-gradient(135deg, rgba(91,78,240,0.6), rgba(0,180,179,0.42)) border-box',
            borderColor: 'transparent'
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '0.85rem',
            padding: '12px 16px',
            borderColor: isDark ? '#2D2B50' : '#E5E7EB'
          },
          head: {
            fontSize: '0.85rem',
            fontWeight: 600,
            backgroundColor: isDark ? '#13112B' : '#F8FAFC',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'all 180ms ease',
            '&:hover': {
              backgroundColor: 'rgba(91,78,240,0.06)'
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'none',
            '&:hover': {
              backgroundColor: 'rgba(91,78,240,0.1)',
              transform: 'none'
            }
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            transition: 'all 200ms ease',
            '&:hover': {
              backgroundColor: 'rgba(91,78,240,0.1)'
            }
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${isDark ? '#2D2B50' : '#E5E7EB'}`
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.28)' : '0 4px 20px rgba(15,23,42,0.08)'
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#F0EFFF' : '#111827',
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: '0.75rem',
            boxShadow: '0 4px 12px rgba(2,6,23,0.2)'
          },
          arrow: {
            color: isDark ? '#F0EFFF' : '#111827'
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${isDark ? '#2D2B50' : '#E5E7EB'}`,
            boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(15,23,42,0.12)'
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            transition: 'all 180ms ease',
            '&.Mui-selected': {
              backgroundColor: 'rgba(91,78,240,0.1)',
              fontWeight: 600
            },
            '&:hover': {
              backgroundColor: 'rgba(91,78,240,0.06)'
            }
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 6,
            backgroundColor: 'rgba(91,78,240,0.12)'
          },
          bar: {
            borderRadius: 4,
            background: 'linear-gradient(90deg, #5B4EF0 0%, #00B4B3 100%)'
          }
        }
      },
      MuiCircularProgress: {
        styleOverrides: {
          circle: {
            strokeLinecap: 'round'
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 46,
            height: 24,
            padding: 0
          },
          switchBase: {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(22px)',
              '& + .MuiSwitch-track': {
                backgroundColor: '#5B4EF0',
                opacity: 1
              }
            }
          },
          thumb: {
            width: 20,
            height: 20,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          },
          track: {
            borderRadius: 50,
            opacity: 1,
            backgroundColor: '#CBD5E1'
          }
        }
      }
    }
  });
};
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
export function App() {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string>('maker');
  const [colorMode, setColorMode] = useState<PaletteMode>(() => localStorage.getItem('openremitColorMode') as PaletteMode || 'light');
  const theme = useMemo(() => createBackofficeTheme(colorMode), [colorMode]);
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    const storedRole = localStorage.getItem('userRole');
    if (authStatus === 'true') {
      setIsLoggedIn(true);
      setUserRole(storedRole || 'maker');
    }
  }, []);
  useEffect(() => {
    const handleColorModeToggle = () => {
      setColorMode(current => {
        const next = current === 'light' ? 'dark' : 'light';
        localStorage.setItem('openremitColorMode', next);
        return next;
      });
    };
    window.addEventListener('openremit-toggle-color-mode', handleColorModeToggle);
    return () => window.removeEventListener('openremit-toggle-color-mode', handleColorModeToggle);
  }, []);
  const handleLogin = (username: string, password: string): boolean => {
    const adminPassword = localStorage.getItem('adminPassword') || 'test@1234';
    const checkerPassword = localStorage.getItem('checkerPassword') || 'test@1234';
    if (username === 'admin' && password === adminPassword || username === 'checker' && password === checkerPassword) {
      const role = username === 'checker' ? 'checker' : 'maker';
      const bankNames = ['Habib Bank Limited (HBL)', 'United Bank Limited (UBL)', 'MCB Bank Limited', 'Allied Bank Limited (ABL)', 'National Bank of Pakistan (NBP)', 'Meezan Bank', 'Bank Alfalah', 'Faysal Bank', 'JS Bank', 'Askari Bank', 'Bank Al Habib', 'Standard Chartered Pakistan', 'Soneri Bank', 'Summit Bank', 'Silk Bank'];
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userRole', role);
      localStorage.setItem('username', username);
      localStorage.setItem('bankName', bankNames[Math.floor(Math.random() * bankNames.length)]);
      setUserRole(role);
      setIsLoggedIn(true);
      return true;
    }
    return false;
  };
  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserRole('maker');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    localStorage.removeItem('bankName');
  };
  return <ThemeProvider theme={theme} data-id="element-0" data-mp="1jdhz8bpl0pti">
      <CssBaseline data-id="element-1" data-mp="1jacawyos1ryu" />
      <ToastProvider data-id="element-2" data-mp="1ob0cgeamtr7b">
        <BrowserRouter data-id="element-3" data-mp="1oagwh34jlcvx">
          <ScrollToTop data-id="element-4" data-mp="1obrvyqqyfyh8" />
          <AppRouter isLoggedIn={isLoggedIn} onLogout={handleLogout} onLogin={handleLogin} data-id="element-5" data-mp="1o95mumof1aww" />
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>;
}